import { useThree } from "@react-three/fiber";
import { useEffect, useMemo, useRef } from "react";
import * as THREE from "three";
import {
	capScaleFigurePlan,
	type FigurePartSpec,
	getFigurePartSpecs,
	planScaleFiguresForScene,
	type ScaleFigureCapacity,
	type ScaleFigurePlacement,
	type ScaleFigurePlan,
} from "./scaleFigureLayout";

/**
 * Stylised seated silhouettes on the benches, so the millimetres in the
 * panel turn into "three people fit".
 *
 * A viewer-layer annotation, not engine geometry: nothing here reaches the
 * BOM, the mesh budget, or the offscreen build-step snapshots (those build
 * their own scene from `buildSauna3D`). The live share screenshot renders
 * the same canvas, so the figures do come along when the user has them on
 * — which is the intent.
 *
 * Static meshes only: no `useFrame`, no animation. A toggle or a rebuild
 * invalidates the demand loop exactly once. The proportions live in
 * `scaleFigureLayout.ts` and every mesh here is built from the same specs
 * the keep-out filter measures, so what is drawn and what is tested for
 * collisions can never drift apart.
 */

/**
 * Muted sage from the forest-green side of the palette. It sits quietly
 * against pale aspen benches and reads as a measuring aid rather than a
 * doll-house figure — the model stays the hero.
 */
const FIGURE_COLOR = 0x6f7f66;

const CAP_SEGMENTS = 4;
const RADIAL_SEGMENTS = 10;
const SPHERE_WIDTH_SEGMENTS = 14;
const SPHERE_HEIGHT_SEGMENTS = 10;

function geometryKey(spec: FigurePartSpec): string {
	return `${spec.shape}:${spec.radius}:${Math.round(spec.length * 1000)}`;
}

function createPartGeometry(spec: FigurePartSpec): THREE.BufferGeometry {
	return spec.shape === "sphere"
		? new THREE.SphereGeometry(
				spec.radius,
				SPHERE_WIDTH_SEGMENTS,
				SPHERE_HEIGHT_SEGMENTS,
			)
		: new THREE.CapsuleGeometry(
				spec.radius,
				spec.length,
				CAP_SEGMENTS,
				RADIAL_SEGMENTS,
			);
}

/**
 * Bring a cache up to date with the plan, in place. All figures share one
 * set of body parts; only the shin varies, and only by bench — so a
 * rebuild that changes nothing but the bench POSITIONS (the common case
 * during a dimension drag) finds every key already there and allocates
 * nothing.
 */
function syncFigureGeometries(
	cache: Map<string, THREE.BufferGeometry>,
	plan: ScaleFigurePlan,
): Map<string, THREE.BufferGeometry> {
	const needed = new Set<string>();
	for (const figure of plan.figures) {
		for (const spec of getFigurePartSpecs(figure.shinLength)) {
			const key = geometryKey(spec);
			needed.add(key);
			if (cache.has(key)) continue;
			cache.set(key, createPartGeometry(spec));
		}
	}
	// A shin length that has left the plan — a bench that moved away from
	// the floor and got its full-length shins back — must not keep its GPU
	// buffer alive for the rest of the session.
	for (const [key, geometry] of cache) {
		if (needed.has(key)) continue;
		geometry.dispose();
		cache.delete(key);
	}
	return cache;
}

function ScaleFigure({
	figure,
	material,
	geometries,
}: {
	figure: ScaleFigurePlacement;
	material: THREE.Material;
	geometries: Map<string, THREE.BufferGeometry>;
}) {
	return (
		<group
			name={`overlay/scale-figure/${figure.key}`}
			position={figure.position}
			rotation={[0, figure.rotationY, 0]}
		>
			{getFigurePartSpecs(figure.shinLength).map((spec) => {
				const geometry = geometries.get(geometryKey(spec));
				if (!geometry) return null;
				return (
					<mesh
						key={spec.id}
						geometry={geometry}
						material={material}
						position={spec.center}
						// A capsule is modelled along Y; lay the thighs along the
						// figure's forward axis.
						rotation={spec.axis === "z" ? [Math.PI / 2, 0, 0] : [0, 0, 0]}
						// Geometry and material lifecycles are owned by the memos
						// below — R3F must not dispose them on a hide/show toggle.
						dispose={null}
					/>
				);
			})}
		</group>
	);
}

interface ScaleFiguresProps {
	visible: boolean;
	/**
	 * Bumped by `SceneCanvas` after every dirty-tracked rebuild. Changing a
	 * dimension replaces the bench meshes, so the figures re-measure from
	 * this signal instead of polling the scene.
	 */
	modelRevision: number;
	onCapacityChange?: (capacity: ScaleFigureCapacity | null) => void;
	/**
	 * Upper bound on the total figure count, from the wizard's size-tier
	 * choice. `null`/`undefined` (no hint) leaves the geometric estimate as
	 * is.
	 */
	maxFigures?: number | null;
}

export function ScaleFigures({
	visible,
	modelRevision,
	onCapacityChange,
	maxFigures,
}: ScaleFiguresProps) {
	const scene = useThree((state) => state.scene);
	const invalidate = useThree((state) => state.invalidate);

	const plan = useMemo(() => {
		if (!visible) return null;
		// `modelRevision` is the rebuild signal, not a value we read.
		void modelRevision;
		const raw = planScaleFiguresForScene(scene ?? null);
		return raw ? capScaleFigurePlan(raw, maxFigures) : raw;
	}, [scene, visible, modelRevision, maxFigures]);

	// Nothing is allocated until there is actually a figure to draw, so a
	// canvas that never shows them — a build-step snapshot canvas, or a
	// viewer with the layer off — pays nothing.
	const hasFigures = (plan?.figures.length ?? 0) > 0;

	// The material outlives individual plans on purpose: a dimension drag
	// rebuilds the plan once per animation frame, and a fresh material
	// would mean a fresh shader compile on every one of those frames.
	const material = useMemo(
		() =>
			hasFigures
				? new THREE.MeshStandardMaterial({
						color: FIGURE_COLOR,
						roughness: 0.95,
						metalness: 0,
					})
				: null,
		[hasFigures],
	);
	useEffect(() => () => material?.dispose(), [material]);

	// Same argument as the material above, and for the same reason: `plan` is
	// a new object after every rebuild even when the key set is identical, so
	// keying the geometries on it threw away and re-uploaded all five buffers
	// on every frame of a dimension drag. The cache lives in a ref and only
	// the keys that actually changed are built — and it stays empty until
	// there is a figure to draw, so a canvas that never shows them (a
	// build-step snapshot, or the layer switched off) still pays nothing.
	const geometryCacheRef = useRef<Map<string, THREE.BufferGeometry> | null>(
		null,
	);
	const geometries = useMemo(() => {
		if (!plan || !hasFigures) return null;
		const cache =
			geometryCacheRef.current ?? new Map<string, THREE.BufferGeometry>();
		geometryCacheRef.current = cache;
		return syncFigureGeometries(cache, plan);
	}, [plan, hasFigures]);
	useEffect(
		() => () => {
			const cache = geometryCacheRef.current;
			geometryCacheRef.current = null;
			if (!cache) return;
			for (const geometry of cache.values()) geometry.dispose();
			cache.clear();
		},
		[],
	);

	// Report the scalars, not the plan: `plan` gets a fresh identity on every
	// `modelRevision` bump, so passing a new object through would commit a
	// state update - and re-render the whole viewer tree - per rebuild even
	// when the seat count never moved.
	const seats = plan?.seats ?? null;
	const relaxSeats = plan?.relaxSeats ?? null;
	useEffect(() => {
		onCapacityChange?.(
			seats === null || relaxSeats === null ? null : { seats, relaxSeats },
		);
	}, [seats, relaxSeats, onCapacityChange]);

	// Demand loop: a toggle or a rebuilt plan asks for exactly one frame.
	// While the figures are off, a scene rebuild leaves the plan `null` and
	// this never fires — `SaunaModel` already invalidated for the rebuild.
	// biome-ignore lint/correctness/useExhaustiveDependencies: `visible` and `plan` are change signals, not values read in the body — one frame per toggle or re-measure.
	useEffect(() => {
		invalidate?.();
	}, [visible, plan, invalidate]);

	if (!plan || !material || !geometries) return null;

	return (
		<group name="overlay/scale-figures">
			{plan.figures.map((figure) => (
				<ScaleFigure
					key={figure.key}
					figure={figure}
					material={material}
					geometries={geometries}
				/>
			))}
		</group>
	);
}
